import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Mail, Globe, ChevronRight, Zap, BarChart3, Target, Volume2, Star, ArrowRight, X, Upload, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BACKEND_URL } from "../context/AuthContext";

const PLANS = [
  {
    name: "Starter",
    price: 39,
    perDay: "1.30",
    color: "#22c55e",
    features: ["1-2 Plays Per Day", "Test Your Ad", "Entry-Level Exposure"],
    impressions: "5,000+",
    cta: "Try It Out",
  },
  {
    name: "Basic",
    price: 75,
    perDay: "2.50",
    color: "#ef4444",
    features: ["1 Play Per Hour", "~700 Plays / Month", "Standard Rotation"],
    impressions: "15,000+",
    cta: "Get Started",
  },
  {
    name: "Heavy",
    price: 200,
    perDay: "6.66",
    color: "#f59e0b",
    features: ["2-3 Plays Per Hour", "High Exposure", "Priority Rotation"],
    impressions: "30,000+",
    cta: "Go Heavy",
  },
  {
    name: "Sponsored",
    price: 300,
    perDay: "10.00",
    color: "#a855f7",
    features: ['"This Hour Powered by..."', "Branded Hour Block", "Premium Mentions"],
    impressions: "50,000+",
    cta: "Sponsor an Hour",
    popular: true,
  },
  {
    name: "Takeover",
    price: 750,
    perDay: "25.00",
    color: "#ef4444",
    features: ["Plays Every 15 Min", "Maximum Visibility", "Exclusive Dominance"],
    impressions: "100,000+",
    cta: "Take Over",
  },
];

const BENEFITS = [
  { icon: BarChart3, title: "Thousands of Monthly Impressions", desc: "Your ad reaches engaged listeners around the clock across our broadcast network." },
  { icon: Target, title: "Targeted Music Audience", desc: "Connect with real fans of hip-hop, R&B, soul, and urban culture — not bots." },
  { icon: Volume2, title: "Consistent Brand Exposure", desc: "Repeated plays build familiarity and trust with your audience over time." },
  { icon: Zap, title: "Real Radio Exposure", desc: "Your brand in rotation on a real broadcast — not a playlist algorithm." },
];

export default function AdvertisePage() {
  const navigate = useNavigate();
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [slots, setSlots] = useState(null);

  // Fetch live slot availability
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ad-bookings/slots-available`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSlots(d); })
      .catch(() => {});
  }, []);

  // Booking modal state
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPlan, setBookingPlan] = useState(null);
  const [bookingStep, setBookingStep] = useState(1); // 1=form, 2=upload, 3=pay, 4=done
  const [bookingData, setBookingData] = useState({ business_name: "", email: "", phone: "", message: "" });
  const [bookingId, setBookingId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const fileRef = useRef(null);

  const openBooking = (plan) => {
    setBookingPlan(plan);
    setBookingStep(1);
    setBookingData({ business_name: "", email: "", phone: "", message: "" });
    setBookingId(null);
    setAudioFile(null);
    setBookingError("");
    setBookingOpen(true);
  };

  const submitBookingForm = async () => {
    if (!bookingData.business_name || !bookingData.email) {
      setBookingError("Business name and email are required.");
      return;
    }
    setSubmitting(true);
    setBookingError("");
    try {
      const fd = new FormData();
      fd.append("business_name", bookingData.business_name);
      fd.append("email", bookingData.email);
      fd.append("phone", bookingData.phone);
      fd.append("message", bookingData.message);
      fd.append("plan", bookingPlan.name.toLowerCase());
      const res = await fetch(`${BACKEND_URL}/api/ad-bookings/create`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      setBookingId(data.booking_id);
      setBookingStep(2);
    } catch (e) {
      setBookingError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadAudio = async () => {
    if (!audioFile || !bookingId) { setBookingStep(3); return; }
    setUploading(true);
    setBookingError("");
    try {
      const fd = new FormData();
      fd.append("file", audioFile);
      const res = await fetch(`${BACKEND_URL}/api/ad-bookings/upload-audio/${bookingId}`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      setBookingStep(3);
    } catch (e) {
      setBookingError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const createPaypalOrder = async () => {
    const fd = new FormData();
    fd.append("booking_id", bookingId);
    const res = await fetch(`${BACKEND_URL}/api/ad-bookings/paypal/create-order`, { method: "POST", body: fd });
    if (!res.ok) throw new Error("Failed to create payment");
    const data = await res.json();
    // Redirect to PayPal approval URL
    if (data.approval_url) {
      window.location.href = data.approval_url;
    }
  };

  const handleBookSlot = (planName) => {
    const plan = PLANS.find(p => p.name === planName);
    if (plan) openBooking(plan);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="advertise-page">
      <SEO title="Advertise With Us — DJsandMCMedia" description="Get your brand in rotation on real internet radio. Up to 50,000+ monthly impressions. Packages starting at $39/month." />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-600/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-red-800/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold uppercase tracking-widest mb-6">
            <Radio className="w-3.5 h-3.5" />
            The Sound of the Culture
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4" data-testid="advertise-headline">
            Advertise Where Your<br />
            Audience Is{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-400">
              Listening
            </span>
          </h1>

          <p className="text-lg text-white/50 max-w-2xl mx-auto mb-4">
            Your brand in rotation. Real radio exposure.<br />
            Up to 50,000+ monthly impressions. Starting at just $1.30/day.
          </p>
          <p className="text-sm text-white/30 max-w-lg mx-auto mb-10">
            Your ad plays every day — not just seen once, but heard repeatedly.
          </p>

          <a
            href="#plans"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]"
            data-testid="advertise-cta-hero"
          >
            Book Your Slot <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "50,000+", label: "Up To Monthly Impressions" },
            { value: "24/7", label: "On-Air Broadcast" },
            { value: "$1.30", label: "Per Day Starting Cost" },
            { value: "Real", label: "Listeners, Not Bots" },
          ].map(({ value, label }) => (
            <div key={label}>
              <p className="text-2xl sm:text-3xl font-black text-red-400">{value}</p>
              <p className="text-white/40 text-xs mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold text-white mb-2">Get Your Brand In Rotation</h2>
            <p className="text-white/40 text-sm">Repetition builds recognition. Pick the rotation that fits your goals.</p>
          </div>
          {/* Live Slots Counter */}
          {slots && (
            <div className="flex items-center justify-center gap-3 mb-10" data-testid="slots-counter">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                background: slots.remaining_slots <= 2 ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
                borderColor: slots.remaining_slots <= 2 ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)",
              }}>
                <AlertCircle className="w-3.5 h-3.5" style={{ color: slots.remaining_slots <= 2 ? "#ef4444" : "#f59e0b" }} />
                <span className="text-sm font-semibold" style={{ color: slots.remaining_slots <= 2 ? "#ef4444" : "#f59e0b" }}>
                  {slots.remaining_slots} of {slots.total_slots} ad slots remaining per hour
                </span>
                {slots.active_advertisers > 0 && (
                  <span className="text-white/30 text-xs ml-1">
                    — {slots.active_advertisers} active advertiser{slots.active_advertisers !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          )}
          {!slots && <p className="text-center text-white/20 text-xs mb-10">Limited ad slots available per hour</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border transition-all duration-300 overflow-hidden group ${plan.popular ? "lg:scale-105 lg:z-10 shadow-[0_0_30px_rgba(168,85,247,0.15)]" : ""}`}
                style={{
                  background: plan.popular
                    ? `linear-gradient(160deg, ${plan.color}15, transparent 60%)`
                    : hoveredPlan === plan.name ? `linear-gradient(160deg, ${plan.color}10, transparent 60%)` : "rgba(255,255,255,0.03)",
                  borderColor: plan.popular
                    ? `${plan.color}50`
                    : hoveredPlan === plan.name ? `${plan.color}40` : "rgba(255,255,255,0.08)",
                  transform: plan.popular ? undefined : hoveredPlan === plan.name ? "translateY(-4px)" : "none",
                }}
                onMouseEnter={() => setHoveredPlan(plan.name)}
                onMouseLeave={() => setHoveredPlan(null)}
                data-testid={`plan-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-bl-xl" style={{ background: plan.color, color: "#fff" }}>
                    Most Popular
                  </div>
                )}
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: plan.color }}>{plan.name}</p>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-black text-white">${plan.price}</span>
                    <span className="text-white/30 text-sm">/mo</span>
                  </div>
                  <p className="text-xs mb-4" style={{ color: plan.color }}>Only ${plan.perDay}/day</p>
                  <div className="rounded-lg px-3 py-2 mb-5 text-center" style={{ background: `${plan.color}10`, border: `1px solid ${plan.color}20` }}>
                    <p className="text-xs font-bold" style={{ color: plan.color }}>{plan.impressions} impressions/mo</p>
                  </div>
                  <div className="space-y-3 mb-6">
                    {plan.features.map((f) => (
                      <div key={f} className="flex items-center gap-2.5 text-sm text-white/60">
                        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: plan.color }} />
                        {f}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => handleBookSlot(plan.name)}
                    className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02]"
                    style={{
                      background: (plan.popular || hoveredPlan === plan.name) ? plan.color : "rgba(255,255,255,0.06)",
                      color: (plan.popular || hoveredPlan === plan.name) ? "#fff" : "rgba(255,255,255,0.7)",
                      boxShadow: (plan.popular || hoveredPlan === plan.name) ? `0 0 20px ${plan.color}30` : "none",
                    }}
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="border-t border-white/5 pt-12 pb-4">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-white/30 text-sm">Ads played across our live 24/7 radio broadcast — used by DJs, brands & artists across DJsandMCMedia</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-lg font-bold text-white mb-2">Why Advertise With Us</h2>
            <p className="text-white/40 text-sm">Real radio. Real audience. Real results.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-1">{title}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="rounded-2xl border border-red-500/20 p-10 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(239,68,68,0.08), transparent 60%)" }}>
            <div className="absolute top-0 right-0 w-[300px] h-[200px] bg-red-600/10 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative">
              <Star className="w-8 h-8 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Ready to Get Your Brand on Air?</h2>
              <p className="text-white/40 text-sm mb-8 max-w-md mx-auto">
                Book your advertising slot today. Up to 50,000+ monthly impressions with real radio exposure. Start for just $1.30/day.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="mailto:gary@djsandmc.com?subject=Advertising%20Inquiry"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]"
                  data-testid="advertise-cta-bottom"
                >
                  <Mail className="w-4 h-4" /> Book Your Slot Now
                </a>
                <a
                  href="https://djsandmcmedia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/10 hover:border-white/20 text-white/60 hover:text-white text-sm transition"
                >
                  <Globe className="w-4 h-4" /> djsandmcmedia.com
                </a>
              </div>
              <p className="text-white/20 text-xs mt-8 italic">Streams don't build wealth... ownership does.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      {bookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setBookingOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 overflow-hidden" style={{ background: "#111" }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <div>
                <p className="text-white font-bold text-sm">Book Your Ad Slot</p>
                {bookingPlan && (
                  <p className="text-xs mt-0.5" style={{ color: bookingPlan.color }}>
                    {bookingPlan.name} Plan — ${bookingPlan.price}/mo
                  </p>
                )}
              </div>
              <button onClick={() => setBookingOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition" data-testid="booking-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Steps indicator */}
            <div className="flex items-center gap-1 px-6 pt-4">
              {["Details", "Audio Spot", "Payment"].map((label, i) => (
                <div key={label} className="flex-1 flex items-center gap-1">
                  <div className={`h-1 flex-1 rounded-full transition-all ${bookingStep > i + 1 ? "bg-green-500" : bookingStep === i + 1 ? "bg-red-500" : "bg-white/10"}`} />
                  <span className={`text-[10px] ${bookingStep >= i + 1 ? "text-white/60" : "text-white/20"}`}>{label}</span>
                </div>
              ))}
            </div>

            <div className="p-6">
              {bookingError && (
                <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs" data-testid="booking-error">
                  {bookingError}
                </div>
              )}

              {/* Step 1: Form */}
              {bookingStep === 1 && (
                <div className="space-y-4" data-testid="booking-step-1">
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">Business / Brand Name *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-red-500/50 transition"
                      value={bookingData.business_name}
                      onChange={e => setBookingData(d => ({ ...d, business_name: e.target.value }))}
                      placeholder="Your business or brand name"
                      data-testid="booking-business-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-red-500/50 transition"
                      value={bookingData.email}
                      onChange={e => setBookingData(d => ({ ...d, email: e.target.value }))}
                      placeholder="you@business.com"
                      data-testid="booking-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">Phone (optional)</label>
                    <input
                      type="tel"
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-red-500/50 transition"
                      value={bookingData.phone}
                      onChange={e => setBookingData(d => ({ ...d, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      data-testid="booking-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 block mb-1.5">Message (optional)</label>
                    <textarea
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-red-500/50 transition resize-none"
                      rows={3}
                      value={bookingData.message}
                      onChange={e => setBookingData(d => ({ ...d, message: e.target.value }))}
                      placeholder="Any special instructions or notes..."
                      data-testid="booking-message"
                    />
                  </div>
                  <button
                    onClick={submitBookingForm}
                    disabled={submitting}
                    className="w-full py-3 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-500 text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="booking-submit-details"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Continue to Audio Upload"}
                  </button>
                </div>
              )}

              {/* Step 2: Audio Upload */}
              {bookingStep === 2 && (
                <div className="space-y-4" data-testid="booking-step-2">
                  <p className="text-white/50 text-sm">Upload your ad spot (MP3, WAV — max 60 seconds, 15MB). You can also skip this and send it later.</p>
                  <div
                    className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-red-500/30 transition"
                    onClick={() => fileRef.current?.click()}
                    data-testid="booking-upload-area"
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept="audio/mp3,audio/wav,audio/m4a,audio/ogg,.mp3,.wav,.m4a,.ogg"
                      className="hidden"
                      onChange={e => setAudioFile(e.target.files[0] || null)}
                    />
                    {audioFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-400">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium">{audioFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/20 mx-auto mb-2" />
                        <p className="text-white/40 text-sm">Click to select your audio file</p>
                        <p className="text-white/20 text-xs mt-1">MP3, WAV, M4A, OGG — Max 15MB</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setAudioFile(null); setBookingStep(3); }}
                      className="flex-1 py-3 rounded-xl text-sm font-medium bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition"
                      data-testid="booking-skip-audio"
                    >
                      Skip — Send Later
                    </button>
                    <button
                      onClick={uploadAudio}
                      disabled={!audioFile || uploading}
                      className="flex-1 py-3 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-500 text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="booking-upload-btn"
                    >
                      {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : "Upload & Continue"}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {bookingStep === 3 && (
                <div className="space-y-5 text-center" data-testid="booking-step-3">
                  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-5">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Order Summary</p>
                    <p className="text-white font-bold">{bookingData.business_name}</p>
                    <p className="text-sm mt-1" style={{ color: bookingPlan?.color }}>{bookingPlan?.name} Plan</p>
                    <p className="text-3xl font-black text-white mt-3">${bookingPlan?.price}<span className="text-white/30 text-sm font-normal">/mo</span></p>
                    <p className="text-white/30 text-xs mt-1">{bookingPlan?.features?.join(" · ")}</p>
                  </div>
                  <button
                    onClick={createPaypalOrder}
                    className="w-full py-3.5 rounded-xl text-sm font-bold bg-[#0070ba] hover:bg-[#005ea6] text-white transition flex items-center justify-center gap-2"
                    data-testid="booking-paypal-btn"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
                    Pay with PayPal
                  </button>
                  <p className="text-white/20 text-xs">You'll be redirected to PayPal to complete your payment securely.</p>
                </div>
              )}

              {/* Step 4: Done */}
              {bookingStep === 4 && (
                <div className="text-center py-6" data-testid="booking-step-4">
                  <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-white font-bold text-lg mb-2">Booking Confirmed!</h3>
                  <p className="text-white/40 text-sm mb-6">
                    Your ad booking has been received. We'll get your spot into rotation and confirm via email.
                  </p>
                  <button
                    onClick={() => setBookingOpen(false)}
                    className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition"
                    data-testid="booking-done-btn"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
